import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import { ContinueButton } from '../../components/ContinueButton';
import { useRsi } from '../../hooks/useRsi';
import { addErrorsAndRunHooks } from './utils/dataMutations';
import { generateColumns } from './components/columns';
import { Table } from '../../components/Table';
import { SubmitDataAlert } from '../../components/Alerts/SubmitDataAlert';
import type { Meta } from './types';
import type { Data } from '../../types';
import { Box, Button, styled, Typography, Dialog, Switch, LinearProgress, Fade } from '@beeline.com/bp-design-system';
import { ModalBody } from '../../components/ModalBody';
import { UserEvents, useUserEvent } from '../../integrations/userEvents/useUserEvent';
import { type RowsChangeData } from '../../ReactDataGrid';

type Props<T extends string> = {
  initialData: (Data<T> & Meta)[];
  file: File;
  onBack?: () => void;
  setHasChanged?: (hasChanged: boolean) => void;
};

export const ValidationStep = <T extends string>({ initialData, file, onBack, setHasChanged }: Props<T>) => {
  const {
    entityType,
    fields: initialFields,
    hasOnlyOneImportType,
    onClose,
    onSubmit,
    rowHook,
    translations,
    onRestart,
    preSubmitHook,
    footerAlert
  } = useRsi<T>();
  const { enqueueSnackbar } = useSnackbar();
  const { track } = useUserEvent();

  const [data, setData] = useState<(Data<T> & Meta)[]>(initialData);
  const [isValidating, setIsValidating] = useState(false);
  const [selectedRows, setSelectedRows] = useState<ReadonlySet<number | string>>(new Set());
  const [filterByErrors, setFilterByErrors] = useState(false);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [showSubmitAlert, setShowSubmitAlert] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [fields, setFields] = useState<typeof initialFields>(initialFields);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Only display fields that are present in the data.
  useEffect(() => {
    if (data.length > 0 && data[0]) {
      const firstDataItemKeys = new Set(Object.keys(data[0]));
      setFields(initialFields.filter((field) => firstDataItemKeys.has(String(field.key))));
    } else {
      setFields(initialFields);
    }
  }, [initialFields, data]);

  const updateData = useCallback(
    async (rows: typeof data, indexes?: number[]) => {
      setHasChanged?.(true);
      // Check if hooks are async - if they are we want to apply changes optimistically for better UX
      if (isAsyncFunction(rowHook)) {
        setIsValidating(true);
        setData(rows);
      }
      // Don't pass in tableHook to improve performance.
      addErrorsAndRunHooks<T>(rows, fields, rowHook, undefined, indexes).then((data) => {
        setData(data);
        setIsValidating(false);
      });
    },
    [rowHook, setHasChanged, fields]
  );

  const deleteSelectedRows = () => {
    if (selectedRows.size) {
      const newData = data.filter((value) => !selectedRows.has(value.__index));
      // No row data changed, only rows removed - skip rowHook revalidation for remaining rows.
      updateData(newData, []);
      setSelectedRows(new Set());
      // Errors-only switch hides itself once no errors remain - clear filter state before that happens,
      // else stale filter keeps hiding rows with no error-only toggle visible to undo it.
      setFilterByErrors(false);
      setErrorsOnly(false);
    }
  };

  const updateRows = useCallback(
    (rows: typeof data, changedData?: RowsChangeData<(typeof data)[number]>) => {
      const changes = changedData?.indexes.reduce(
        (acc, index) => {
          // when data is filtered val !== actual index in data
          const realIndex = data.findIndex((value) => value.__index === rows?.[index]?.__index);
          if (rows[index] !== undefined) {
            acc[realIndex] = rows[index] as Data<T> & Meta;
          }
          return acc;
        },
        {} as Record<number, (typeof data)[number]>
      );
      const realIndexes = changes && Object.keys(changes).map((index) => Number(index));
      const newData = Object.assign([], data, changes);
      updateData(newData, realIndexes);
    },
    [data, updateData]
  );

  const tableData = useMemo(() => {
    if (filterByErrors || errorsOnly) {
      return data.filter((value) => {
        if (!value?.__errors) return false;
        const errorLevels = Object.values(value.__errors);
        const matchesIssues = filterByErrors && errorLevels.some((err) => err.level === 'warning');
        const matchesErrors = errorsOnly && errorLevels.some((err) => err.level === 'error');
        return matchesIssues || matchesErrors;
      });
    }
    return data;
  }, [data, errorsOnly, filterByErrors]);

  const columns = useMemo(() => generateColumns(fields, tableData, selectedRows), [fields, selectedRows, tableData]);

  const hasAnyErrors = useMemo(
    () => data.some((value) => value?.__errors && Object.values(value.__errors).some((err) => err.level === 'error')),
    [data]
  );

  const rowKeyGetter = useCallback((row: Data<T> & Meta) => row.__index, []);

  const submitData = async (dataToSubmit: (Data<T> & Meta)[] = data) => {
    const calculatedData = dataToSubmit.reduce(
      (acc, value) => {
        const { __index, __errors, ...values } = value;
        if (__errors) {
          for (const key in __errors) {
            if (__errors[key]?.level === 'error') {
              acc.invalidData.push(values as unknown as Data<T>);
              return acc;
            }
          }
        }
        acc.validData.push(values as unknown as Data<T>);
        return acc;
      },
      { validData: [] as Data<T>[], invalidData: [] as Data<T>[], all: dataToSubmit }
    );
    setShowSubmitAlert(false);
    setSubmitting(true);
    const response = onSubmit(calculatedData, file);
    if (response?.then) {
      response
        .then(() => {
          track(UserEvents.importerEvents.IMPORT_DATA);
          setShowConfirmDialog(true);
        })
        .catch((err: Error) => {
          enqueueSnackbar(
            <>
              {translations.alerts.submitError.title}
              <br />
              {err?.message || translations.alerts.submitError.defaultMessage}
            </>,
            {
              variant: 'error'
            }
          );
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else {
      onClose();
    }
  };

  const hasErrors = (rows: (Data<T> & Meta)[]) =>
    rows.some((value) => value?.__errors && Object.values(value.__errors).some((err) => err.level === 'error'));

  const onContinue = async () => {
    if (preSubmitHook) {
      const result = await preSubmitHook(data);
      if (result === null) {
        return;
      }
      if (!hasErrors(result)) {
        submitData(result);
      } else {
        setData(result);
        setShowSubmitAlert(true);
      }
      return;
    }
    if (!hasErrors(data)) {
      submitData();
    } else {
      setShowSubmitAlert(true);
    }
  };

  return (
    <>
      <SubmitDataAlert isOpen={showSubmitAlert} onClose={() => setShowSubmitAlert(false)} onConfirm={submitData} />
      <Box display="flex" gap="8px" flexDirection="column">
        <Box display="flex" gap="8px" flexDirection="row" justifyContent="space-between">
          <Typography dataTest="title" variant="h2" pb={2}>
            {translations.validationStep.title}
          </Typography>
          <Box display="flex" gap="16px" alignItems="center" flexWrap="nowrap" whiteSpace="nowrap">
            <Button variant="outlined" size="small" disabled={!selectedRows.size} onClick={deleteSelectedRows}>
              {translations.validationStep.discardButtonTitle}
            </Button>
            <StyledFilterSwitch
              checked={filterByErrors}
              dataTest="filter-switch"
              label={translations.validationStep.filterSwitchTitle}
              onChange={() => setFilterByErrors(!filterByErrors)}
              size="small"
              $hasAnyErrors={hasAnyErrors}
            />
            {hasAnyErrors && (
              <StyledErrorsOnlySwitch
                checked={errorsOnly}
                dataTest="errors-only-checkbox"
                label={translations.validationStep.errorsOnlyCheckboxTitle}
                onChange={() => setErrorsOnly(!errorsOnly)}
                size="small"
              />
            )}
          </Box>
        </Box>
        <StyledSubTitle variant="body1">{translations.validationStep.subTitle}</StyledSubTitle>
      </Box>

      <ModalBody constrainBox pb={0}>
        <Box height={4}>
          <Fade in={isValidating} unmountOnExit>
            <LinearProgress />
          </Fade>
        </Box>
        <Table
          rowKeyGetter={rowKeyGetter}
          rows={tableData}
          onRowsChange={updateRows}
          columns={columns}
          selectedRows={selectedRows}
          editable
          onSelectedRowsChange={(rows) => setSelectedRows(rows as ReadonlySet<string | number>)}
          components={{
            noRowsFallback: (
              <Box display="flex" justifyContent="center" gridColumn="1/-1" mt="32px">
                {filterByErrors
                  ? translations.validationStep.noRowsMessageWhenFiltered
                  : translations.validationStep.noRowsMessage}
              </Box>
            )
          }}
        />
      </ModalBody>
      {footerAlert && <StyledFooterAlertBox>{footerAlert(data)}</StyledFooterAlertBox>}
      <ContinueButton
        isLoading={isSubmitting}
        disabled={isValidating}
        onContinue={onContinue}
        onBack={onBack}
        title={translations.validationStep.nextButtonTitle}
        backTitle={translations.validationStep.backButtonTitle}
        trackStepName="Verify your data"
      />
      <StyledDialog
        open={showConfirmDialog}
        fullScreen
        sx={{ padding: '1.5rem' }}
        onClose={() => {
          track(UserEvents.importerEvents.BACK_TO_SCREEN, { entityType });
          onClose();
        }}
      >
        <StyledContent>
          <Typography variant="label1">{translations.alerts.confirmation.title(entityType)}</Typography>
          <Typography variant="label2">{translations.alerts.confirmation.subTitle}</Typography>
          <StyledButtons>
            <StyledButton
              color="primary"
              variant="contained"
              size="small"
              onClick={() => {
                track(UserEvents.importerEvents.RESTART_IMPORT_DATA);
                onRestart();
              }}
            >
              {translations.alerts.confirmation.importData}
            </StyledButton>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                track(UserEvents.importerEvents.BACK_TO_SCREEN, { entityType });
                onClose();
              }}
            >
              {translations.alerts.confirmation.backToScreen(entityType, hasOnlyOneImportType)}
            </Button>
          </StyledButtons>
        </StyledContent>
      </StyledDialog>
    </>
  );
};

const StyledFooterAlertBox = styled(Box)`
  max-width: calc(100% - 25px);
`;

const StyledFilterSwitch = styled(Switch)<{ $hasAnyErrors: boolean }>`
  .typography-label {
      margin-right: ${({ $hasAnyErrors }) => ($hasAnyErrors ? '-1rem' : '1.25rem')};
  }
`;

const StyledErrorsOnlySwitch = styled(Switch)`
  && {
      margin: 0;
  }

  .typography-label {
      margin-right: 1.25rem;
  }

  [data-test="checkbox-label"] {
      font-size: 0.75rem;
  }
`;

const StyledSubTitle = styled(Typography)`
  && {
      margin-right: 1rem;
  }
`;

const StyledDialog = styled(Dialog)`
  .MuiBackdrop-root {
      opacity: 0 !important;
  }

  [data-test="content_container"] {
      height: 100%;
  }
`;

const StyledContent = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  height: 100%;
`;

const StyledButtons = styled('div')`
  display: flex;
  gap: 1rem;
`;

const StyledButton = styled(Button)`
  margin-right: 1rem;
`;

const AsyncFunction = (async () => {}).constructor;
const isAsyncFunction = (rowHook?: Function): boolean => {
  if (!rowHook) {
    return false;
  }

  return rowHook?.constructor.name === 'AsyncFunction' || rowHook instanceof AsyncFunction;
};
